import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InterviewIcon from '@mui/icons-material/RecordVoiceOver';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { Insight } from '../../types';

const InsightsList: React.FC = () => {
  const navigate = useNavigate();
  const { items, selectedFilters } = useAppSelector((state) => state.insights);
  const [expanded, setExpanded] = useState<string | false>(false);

  const filteredInsights = useMemo(() => {
    if (selectedFilters.length === 0) {
      return items;
    }
    return items.filter((insight) =>
      selectedFilters.some((filterId) =>
        insight.tags.some((tag) => tag.id === filterId)
      )
    );
  }, [items, selectedFilters]);

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
        インサイト一覧
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: 'white' }}>
        {filteredInsights.length} 件のインサイトが見つかりました
      </Typography>

      {filteredInsights.map((insight: Insight) => (
        <Accordion
          key={insight.id}
          expanded={expanded === insight.id}
          onChange={handleChange(insight.id)}
          sx={{
            mb: 2,
            '&:before': {
              display: 'none',
            },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: '8px !important',
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              '&:hover': {
                backgroundColor: 'rgba(63, 81, 181, 0.05)',
              },
              minHeight: 72,
              '&.Mui-expanded': {
                minHeight: 72,
                backgroundColor: 'rgba(63, 81, 181, 0.08)',
              },
            }}
          >
            <Box sx={{ width: '100%', pr: 2 }}>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                {insight.title}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {insight.tags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    sx={{
                      backgroundColor: tag.color,
                      color: 'white',
                      fontSize: '0.75rem',
                      '&:hover': {
                        backgroundColor: tag.color,
                        opacity: 0.8,
                      },
                    }}
                  />
                ))}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {new Date(insight.createdAt).toLocaleDateString('ja-JP')}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              padding: 3,
            }}
          >
            <Box
              sx={{
                '& h2': {
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  mt: 3,
                  mb: 1.5,
                },
                '& h3': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mt: 2,
                  mb: 1,
                },
                '& p': {
                  lineHeight: 1.7,
                  mb: 2,
                },
                '& ul': {
                  paddingLeft: 2,
                  mb: 2,
                },
                '& li': {
                  lineHeight: 1.6,
                  mb: 0.5,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: insight.content
                  .replace(/## (.*)/g, '<h2>$1</h2>')
                  .replace(/### (.*)/g, '<h3>$1</h3>')
                  .replace(/- (.*)/g, '<li>$1</li>')
                  .replace(/\n\n/g, '</p><p>')
                  .replace(/^/, '<p>')
                  .replace(/$/, '</p>')
                  .replace(/<p><\/p>/g, '')
                  .replace(/<p><li>/g, '<ul><li>')
                  .replace(/<\/li><\/p>/g, '</li></ul>')
                  .replace(/<\/li><p><li>/g, '</li><li>')
              }}
            />

            {insight.interviewId && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<InterviewIcon />}
                    onClick={() => navigate(`/interviews/${insight.interviewId}`)}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(63, 81, 181, 0.08)',
                        borderColor: 'primary.dark',
                      },
                    }}
                  >
                    元のインタビューを見る
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {filteredInsights.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            条件に一致するインサイトが見つかりませんでした
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            フィルターを変更して再度お試しください
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InsightsList;
